import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Navigation from "../components/Navigation";
import axios from "axios";
import {
  Bell, Heart, Star, MessageCircle, UserPlus, Share2,
  PlayCircle, Check, Trash2, CheckCheck
} from "lucide-react";

const notificationIcons = {
  reaction: Heart,
  comment: MessageCircle,
  friend_request: UserPlus,
  friend_accepted: UserPlus,
  repost: Share2,
  story_reaction: Heart,
  reel_reaction: PlayCircle,
  default: Bell,
};

const notificationColors = {
  reaction: "text-red-500 bg-red-50",
  comment: "text-blue-500 bg-blue-50",
  friend_request: "text-green-500 bg-green-50",
  friend_accepted: "text-green-500 bg-green-50",
  repost: "text-purple-500 bg-purple-50",
  story_reaction: "text-pink-500 bg-pink-50",
  reel_reaction: "text-orange-500 bg-orange-50",
  default: "text-amber-500 bg-amber-50",
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-read`);
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setNotifications(notifications.filter((n) => n.id !== id));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      await axios.put(`${API}/notifications/${notification.id}/read`);
      setNotifications(
        notifications.map((n) =>
          n.id === notification.id ? { ...n, read: true } : n
        )
      );
    }

    // Navigate based on type
    if (notification.post_id) {
      navigate(`/feed`);
    } else if (notification.from_user_id) {
      navigate(`/profile/${notification.from_user_id}`);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-white rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pb-24">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-amber-600" />
              Notifications
            </h1>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                {unreadCount} unread notification{unreadCount !== 1 && "s"}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No notifications yet
            </h3>
            <p className="text-gray-500">
              When someone interacts with you, you'll see it here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const Icon = notificationIcons[notification.type] || notificationIcons.default;
              const colorClasses = notificationColors[notification.type] || notificationColors.default;

              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-white rounded-xl p-4 flex items-start gap-4 cursor-pointer hover:shadow-md transition-shadow ${
                    !notification.read ? "border-l-4 border-amber-500" : ""
                  }`}
                >
                  {/* User Photo */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                      {notification.from_user_photo ? (
                        <img
                          src={notification.from_user_photo}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-amber-600 font-bold">
                          {notification.from_user_name?.[0]}
                        </span>
                      )}
                    </div>
                    {/* Icon Badge */}
                    <div
                      className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${colorClasses}`}
                    >
                      <Icon className="w-3 h-3" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900">
                      <span className="font-semibold">{notification.from_user_name}</span>{" "}
                      <span className="text-gray-600">
                        {notification.message?.replace(notification.from_user_name, "")}
                      </span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(notification.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
